import React, { useState, useMemo } from 'react';
import { Button } from 'rebass';
import { Label } from '@rebass/forms';
import { Link } from 'react-router-dom';
import { FiSettings, FiXCircle, FiPlus } from 'react-icons/fi';

import { useAddPanelistToPanelMutation } from 'api/useAddPanelistToPanelMutation';
import { usePanelsQuery } from 'api/usePanelsQuery';
import { useRemovePanelistFromPanelMutation } from 'api/useRemovePanelistFromPanelMutation';
import { useToastHelper } from 'hooks/useToastHelper';

import CreatePanel from 'components/Participant/CreatePanel';
import NiceModal from 'components/NiceModal';
import PanelDropdown from 'components/PanelDropdown';

/**
 * Display and manage participant panels.
 * Allows adding/removing a person from panels and creating new panels.
 *
 * @param {Object[]} panels - Panels the person currently belongs to
 * @param {number} personId - ID of the person
 * @param {Function} onUpdate - Callback invoked after a panel is added or removed
 * @param {boolean} showActions - Whether to show add/remove actions
 */
const ParticipantPanels = ({ panels, personId, onUpdate, showActions }) => {
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const [showSelectPanelDropdown, setShowSelectPanelDropdown] = useState(false);
    const [showModalCreatePanel, setShowModalCreatePanel] = useState(false);

    const { data: allPanels } = usePanelsQuery({ enabled: showActions });

    const availablePanels = useMemo(() => {
        if (!allPanels) return [];
        const personPanelIds = new Set((panels || []).map(p => p.id));
        return allPanels.filter(p => !personPanelIds.has(p.id));
    }, [allPanels, panels]);

    const { mutate: removeFromPanel } = useRemovePanelistFromPanelMutation({
        onSuccess: () => {
            addSuccessToast('Panelist successfully removed from panel');
            if (onUpdate) {
                onUpdate();
            }
        },
        onError: () => {
            addErrorToast('Failed to remove panelist from panel');
        }
    });

    const { mutate: addToPanel } = useAddPanelistToPanelMutation({
        onSuccess: () => {
            addSuccessToast('Panelist successfully added to panel');
            if (onUpdate) {
                onUpdate();
            }
        },
        onError: () => {
            addErrorToast('Failed to add panelist to panel');
        }
    });

    const onCreatePanel = panel => {
        addSuccessToast('Successfully created a new panel');
        addToPanel({ personId, panelId: panel.id });
    };

    return (
        <>
            {panels && panels.length > 0 ? (
                <div>
                    {panels.map(panel => (
                        <div key={panel.id} className="row-hover row-basic-grid">
                            <div className="flex">
                                <span className="black grow mt-1">{panel.title}</span>
                                {showActions && (
                                    <>
                                        <Button
                                            as={Link}
                                            to={`/panels/${panel.id}`}
                                            ml={2}
                                            variant="secondary-gray"
                                            className="secondary-icon !py-1 !px-2"
                                            alignItems="flex-start"
                                        >
                                            <FiSettings />
                                        </Button>
                                        <Button
                                            ml={2}
                                            type="button"
                                            variant="secondary-gray"
                                            className="secondary-icon"
                                            onClick={() => removeFromPanel({ personId, panelId: panel.id })}
                                        >
                                            <FiXCircle />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="form-data">
                    <Label>This person does not belong to any panels.</Label>
                </div>
            )}
            {showActions && (
                <>
                    <div className="relative form-row-margin">
                        <Button variant="secondary-gray" onClick={() => setShowSelectPanelDropdown(true)}>
                            <FiPlus /> Add to panel
                        </Button>
                        <PanelDropdown
                            showDropdown={showSelectPanelDropdown}
                            width={260}
                            height={286}
                            items={availablePanels}
                            onChange={panelId => addToPanel({ personId, panelId })}
                            onClose={() => setShowSelectPanelDropdown(false)}
                            onCreatePanel={() => {
                                setShowModalCreatePanel(true);
                                setShowSelectPanelDropdown(false);
                            }}
                        />
                    </div>
                    <NiceModal
                        isOpen={showModalCreatePanel}
                        shouldCloseOnOverlayClick
                        onRequestClose={() => setShowModalCreatePanel(false)}
                        title="Create new panel"
                    >
                        <CreatePanel onCreate={onCreatePanel} onClose={() => setShowModalCreatePanel(false)} />
                    </NiceModal>
                </>
            )}
        </>
    );
};

export default ParticipantPanels;
